import React, { useState, useEffect } from 'react';
import { ChevronDown, ArrowRight, BarChart3, Shield, Users, Zap, Building2, Grid, GraduationCap, TreePine, Landmark, Menu, X, Phone, Mail, Calendar, Download, Play, ChevronRight, Check, Brain, Eye, Lock } from 'lucide-react';

export default function AqylAiWebsite() {
  const [activeIndustry, setActiveIndustry] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    interest: 'general'
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const industries = [
    {
      title: "Manufacturing Intelligence",
      icon: Building2,
      challenges: "Optimize production, reduce downtime, improve quality control",
      benefits: "30% increase in OEE, 45% reduction in defects",
      image: "🏭",
      customer: "Global Auto Corp",
      quote: "AqylAi transformed our production line efficiency"
    },
    {
      title: "Grid Intelligence", 
      icon: Grid,
      challenges: "Balance supply and demand, integrate renewables, prevent outages",
      benefits: "25% energy savings, 60% faster outage response",
      image: "⚡",
      customer: "National Energy Grid",
      quote: "Predictive analytics that actually work"
    },
    {
      title: "Public Service Intelligence",
      icon: Landmark,
      challenges: "Improve citizen services, optimize resources, enhance transparency",
      benefits: "40% faster service delivery, 35% cost reduction",
      image: "🏛️",
      customer: "Metro City Government",
      quote: "AI that citizens can trust"
    },
    {
      title: "Education Intelligence",
      icon: GraduationCap,
      challenges: "Personalize learning, improve outcomes, support at-risk students",
      benefits: "28% improvement in graduation rates",
      image: "🎓",
      customer: "State University System",
      quote: "Finally, AI that helps educators teach better"
    },
    {
      title: "Farm Intelligence",
      icon: TreePine,
      challenges: "Optimize yields, reduce waste, manage resources sustainably",
      benefits: "35% yield increase, 50% water savings",
      image: "🌾",
      customer: "AgriTech Cooperative",
      quote: "Sustainable farming powered by transparent AI"
    }
  ];

  const results = [
    { metric: "97%", label: "Prediction Accuracy" },
    { metric: "3.2x", label: "Average ROI" },
    { metric: "45%", label: "Cost Reduction" },
    { metric: "60%", label: "Faster Decisions" }
  ];

  const navigation = {
    solutions: [
      { section: "By Industry", items: ["Manufacturing Intelligence", "Grid Intelligence", "Public Service Intelligence", "Education Intelligence", "Farm Intelligence"] },
      { section: "By Function", items: ["Decision Intelligence", "Predictive Analytics", "Resource Optimization", "Risk Management", "Performance Monitoring"] }
    ],
    platform: ["Ethical AI Framework", "Transparency & Explainability", "Human-Centered Design", "Integration Capabilities", "Security & Compliance", "Implementation Approach"],
    customers: ["Success Stories", "Case Studies", "Testimonials", "ROI & Results", "Customer Community"],
    resources: ["Blog", "White Papers", "Webinars", "Videos", "ROI Calculators", "Documentation"],
    company: ["About Us", "Leadership", "Careers", "Partners", "News & Events", "Contact Us"]
  };

  const resources = [
    {
      type: "White Paper",
      title: "The Future of Ethical AI in Industry",
      description: "Explore how transparent AI is transforming decision-making across sectors",
      date: "January 2025"
    },
    {
      type: "Webinar",
      title: "Implementing AI That Explains Itself",
      description: "Learn the key principles of explainable AI implementation",
      date: "February 12, 2025"
    },
    {
      type: "Case Study",
      title: "How Metro Energy Reduced Outages by 60%",
      description: "Real-world results from our Grid Intelligence platform",
      date: "January 2025"
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Handle form submission
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-gray-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">AqylAi</h1>
              </div>
              <div className="hidden md:block ml-10">
                <div className="flex items-baseline space-x-4">
                  <div className="relative group">
                    <button className="px-3 py-2 text-sm font-medium hover:text-blue-400 transition-colors flex items-center">
                      Solutions <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                    <div className="absolute left-0 mt-2 w-96 bg-gray-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 p-6">
                      <div className="grid grid-cols-2 gap-6">
                        {navigation.solutions.map((section, idx) => (
                          <div key={idx}>
                            <h3 className="text-sm font-semibold text-gray-400 mb-3">{section.section}</h3>
                            {section.items.map((item, i) => (
                              <a key={i} href="#" className="block py-2 text-sm hover:text-blue-400 transition-colors">{item}</a>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <a href="#platform" className="px-3 py-2 text-sm font-medium hover:text-blue-400 transition-colors">Platform</a>
                  <a href="#customers" className="px-3 py-2 text-sm font-medium hover:text-blue-400 transition-colors">Customers</a>
                  <a href="#resources" className="px-3 py-2 text-sm font-medium hover:text-blue-400 transition-colors">Resources</a>
                  <a href="#company" className="px-3 py-2 text-sm font-medium hover:text-blue-400 transition-colors">Company</a>
                </div>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <a href="#" className="text-sm hover:text-blue-400 transition-colors">Customer Login</a>
              <button className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-all transform hover:scale-105">
                Request Demo
              </button>
            </div>
            <div className="md:hidden">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2">
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-gray-900"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent animate-gradient">
            Ethical AI for Industry-Specific Decision Intelligence
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Transparent AI that works with people, not instead of them
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-4 rounded-lg font-medium text-lg transition-all transform hover:scale-105 shadow-lg">
              Request a Demo
            </button>
            <button className="border border-gray-600 hover:border-gray-500 px-8 py-4 rounded-lg font-medium text-lg transition-all hover:bg-gray-800">
              Explore Solutions
            </button>
          </div>
          <p className="text-gray-400">Trusted by <span className="text-white font-semibold">500+</span> organizations across <span className="text-white font-semibold">5</span> industries</p>
        </div>
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="h-8 w-8 text-gray-400" />
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-2xl hover:shadow-2xl transition-all transform hover:-translate-y-2 border border-gray-700">
              <div className="bg-blue-600/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Brain className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Ethical AI</h3>
              <p className="text-gray-400">AI that explains its recommendations with full transparency, building trust at every decision point.</p>
            </div>
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-2xl hover:shadow-2xl transition-all transform hover:-translate-y-2 border border-gray-700">
              <div className="bg-purple-600/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Users className="h-8 w-8 text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Human-Centered</h3>
              <p className="text-gray-400">Enhancing human expertise, not replacing it. Designed to amplify your team's capabilities.</p>
            </div>
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-2xl hover:shadow-2xl transition-all transform hover:-translate-y-2 border border-gray-700">
              <div className="bg-pink-600/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Zap className="h-8 w-8 text-pink-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Industry-Specific</h3>
              <p className="text-gray-400">Solutions tailored to your unique challenges, not generic AI adapted to your needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="py-20 bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Industry-Specific Intelligence</h2>
          <p className="text-xl text-gray-400 text-center mb-12 max-w-3xl mx-auto">
            Purpose-built AI solutions that understand your industry's unique challenges and opportunities
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {industries.map((industry, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndustry(idx)}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeIndustry === idx 
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg' 
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                <industry.icon className="inline-block h-5 w-5 mr-2" />
                {industry.title}
              </button>
            ))}
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-6xl mb-6">{industries[activeIndustry].image}</div>
                <h3 className="text-3xl font-bold mb-4">{industries[activeIndustry].title}</h3>
                <p className="text-gray-400 mb-6">{industries[activeIndustry].challenges}</p>
                <div className="bg-gray-800 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-400 mb-2">Key Benefits:</p>
                  <p className="text-lg font-semibold text-blue-400">{industries[activeIndustry].benefits}</p>
                </div>
                <button className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-medium transition-all inline-flex items-center">
                  Learn More <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gray-700 rounded-full mr-4"></div>
                  <div>
                    <p className="font-semibold">{industries[activeIndustry].customer}</p>
                    <p className="text-sm text-gray-400">Customer Success</p>
                  </div>
                </div>
                <blockquote className="text-lg italic text-gray-300">
                  "{industries[activeIndustry].quote}"
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Showcase */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Proven Results Across Industries</h2>
          <p className="text-xl text-gray-400 text-center mb-12 max-w-3xl mx-auto">
            Real impact delivered through ethical, transparent AI
          </p>
          
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {results.map((result, idx) => (
              <div key={idx} className="text-center group">
                <div className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">
                  {result.metric}
                </div>
                <p className="text-gray-400">{result.label}</p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl border border-gray-700 hover:shadow-xl transition-all">
              <div className="flex items-center mb-4">
                <Building2 className="h-8 w-8 text-blue-400 mr-3" />
                <div>
                  <h4 className="font-semibold">AutoTech Manufacturing</h4>
                  <p className="text-sm text-gray-400">30% increase in OEE</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm">Reduced unplanned downtime by 45% using predictive maintenance AI</p>
            </div>
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl border border-gray-700 hover:shadow-xl transition-all">
              <div className="flex items-center mb-4">
                <Grid className="h-8 w-8 text-purple-400 mr-3" />
                <div>
                  <h4 className="font-semibold">National Power Grid</h4>
                  <p className="text-sm text-gray-400">60% faster outage response</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm">AI-powered grid optimization saved $12M annually in energy costs</p>
            </div>
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl border border-gray-700 hover:shadow-xl transition-all">
              <div className="flex items-center mb-4">
                <Landmark className="h-8 w-8 text-pink-400 mr-3" />
                <div>
                  <h4 className="font-semibold">Metro City Services</h4>
                  <p className="text-sm text-gray-400">40% faster service delivery</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm">Citizen satisfaction increased 35% with transparent AI decisions</p>
            </div>
          </div>

          <div className="text-center mt-8">
            <button className="text-blue-400 hover:text-blue-300 font-medium inline-flex items-center transition-colors">
              See All Customer Stories <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Platform Overview */}
      <section id="platform" className="py-20 bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">The AqylAi Platform</h2>
          <p className="text-xl text-gray-400 text-center mb-12 max-w-3xl mx-auto">
            Built on principles of transparency, ethics, and human collaboration
          </p>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 mb-12 border border-gray-700">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800 p-6 rounded-lg hover:bg-gray-700 transition-colors">
                    <Eye className="h-10 w-10 text-blue-400 mb-3" />
                    <h4 className="font-semibold mb-2">Transparent Decisions</h4>
                    <p className="text-sm text-gray-400">Every recommendation comes with clear explanations</p>
                  </div>
                  <div className="bg-gray-800 p-6 rounded-lg hover:bg-gray-700 transition-colors">
                    <Shield className="h-10 w-10 text-purple-400 mb-3" />
                    <h4 className="font-semibold mb-2">Ethical Framework</h4>
                    <p className="text-sm text-gray-400">Built-in bias detection and fairness monitoring</p>
                  </div>
                  <div className="bg-gray-800 p-6 rounded-lg hover:bg-gray-700 transition-colors">
                    <Users className="h-10 w-10 text-pink-400 mb-3" />
                    <h4 className="font-semibold mb-2">Human-in-the-Loop</h4>
                    <p className="text-sm text-gray-400">Designed for collaboration, not replacement</p>
                  </div>
                  <div className="bg-gray-800 p-6 rounded-lg hover:bg-gray-700 transition-colors">
                    <Lock className="h-10 w-10 text-green-400 mb-3" />
                    <h4 className="font-semibold mb-2">Enterprise Security</h4>
                    <p className="text-sm text-gray-400">SOC 2, HIPAA, and GDPR compliant</p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-gray-800 rounded-lg p-8 shadow-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-semibold">AI Decision Dashboard</h5>
                    <span className="text-xs text-green-400">Live</span>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded p-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm">Prediction Confidence</span>
                        <span className="text-sm font-semibold text-blue-400">94.7%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full" style={{width: '94.7%'}}></div>
                      </div>
                    </div>
                    <div className="bg-gray-900 rounded p-3">
                      <p className="text-xs text-gray-400 mb-1">Decision Factors</p>
                      <div className="space-y-1">
                        <div className="flex items-center text-sm">
                          <Check className="h-4 w-4 text-green-400 mr-2" />
                          <span>Historical pattern match (87%)</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Check className="h-4 w-4 text-green-400 mr-2" />
                          <span>Sensor data correlation (92%)</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Check className="h-4 w-4 text-green-400 mr-2" />
                          <span>Environmental factors (78%)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-4 rounded-lg font-medium text-lg transition-all transform hover:scale-105 shadow-lg">
              Explore the Platform
            </button>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 rounded-2xl p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full filter blur-3xl"></div>
            <div className="relative">
              <div className="w-20 h-20 bg-gray-700 rounded-full mx-auto mb-6"></div>
              <blockquote className="text-2xl font-light mb-6 text-gray-300">
                "AqylAi didn't just give us AI—they gave us AI we can trust. The transparency and explainability have been game-changers for getting buy-in from our teams and stakeholders."
              </blockquote>
              <div>
                <p className="font-semibold text-lg">Sarah Chen</p>
                <p className="text-gray-400">Chief Technology Officer, Global Manufacturing Inc.</p>
              </div>
              <button className="mt-6 text-blue-400 hover:text-blue-300 font-medium inline-flex items-center transition-colors">
                Read the Full Story <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section id="resources" className="py-20 bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Latest Insights</h2>
          <p className="text-xl text-gray-400 text-center mb-12 max-w-3xl mx-auto">
            Stay informed with our latest thinking on ethical AI and industry transformation
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {resources.map((resource, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-2 border border-gray-700">
                <div className="h-48 bg-gradient-to-br from-blue-600/20 to-purple-600/20 flex items-center justify-center">
                  {resource.type === "White Paper" && <Download className="h-16 w-16 text-blue-400" />}
                  {resource.type === "Webinar" && <Play className="h-16 w-16 text-purple-400" />}
                  {resource.type === "Case Study" && <BarChart3 className="h-16 w-16 text-pink-400" />}
                </div>
                <div className="p-6">
                  <span className="text-sm text-blue-400 font-medium">{resource.type}</span>
                  <h3 className="text-xl font-semibold mt-2 mb-3">{resource.title}</h3>
                  <p className="text-gray-400 mb-4">{resource.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{resource.date}</span>
                    <button className="text-blue-400 hover:text-blue-300 font-medium inline-flex items-center transition-colors">
                      {resource.type === "Webinar" ? "Register" : "Download"} <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="text-blue-400 hover:text-blue-300 font-medium inline-flex items-center transition-colors text-lg">
              View All Resources <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Ready to Transform Your Decision Intelligence?</h2>
          <p className="text-xl text-gray-400 text-center mb-12">
            Let's discuss how ethical AI can drive value for your organization
          </p>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700">
            <form onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Interest</label>
                  <select
                    value={formData.interest}
                    onChange={(e) => setFormData({...formData, interest: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                  >
                    <option value="general">General Information</option>
                    <option value="manufacturing">Manufacturing Intelligence</option>
                    <option value="energy">Grid Intelligence</option>
                    <option value="public">Public Service Intelligence</option>
                    <option value="education">Education Intelligence</option>
                    <option value="agriculture">Farm Intelligence</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-4 rounded-lg font-medium text-lg transition-all transform hover:scale-[1.02] shadow-lg"
                  >
                    Request a Demo
                  </button>
                </div>
              </div>
            </form>

            <div className="mt-8 pt-8 border-t border-gray-700">
              <p className="text-center text-gray-400 mb-4">Or contact us directly</p>
              <div className="flex flex-wrap justify-center gap-6">
                <a href="#" className="flex items-center text-gray-300 hover:text-blue-400 transition-colors">
                  <Phone className="h-5 w-5 mr-2" />
                  +1 (555) 123-4567
                </a>
                <a href="#" className="flex items-center text-gray-300 hover:text-blue-400 transition-colors">
                  <Mail className="h-5 w-5 mr-2" />
                  contact@aqylai.com
                </a>
                <button className="flex items-center text-gray-300 hover:text-blue-400 transition-colors">
                  <Calendar className="h-5 w-5 mr-2" />
                  Schedule a Call
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent mb-4">AqylAi</h3>
              <p className="text-gray-400 mb-6">Ethical AI for Industry-Specific Decision Intelligence</p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">LinkedIn</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Twitter</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">YouTube</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Solutions</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Manufacturing</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Energy & Grid</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Public Sector</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Education</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Agriculture</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Platform</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Ethical AI</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Transparency</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Integration</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Security</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Documentation</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Partners</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">© 2025 AqylAi. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Terms of Use</a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}